const supportsCollapsedGroups = 'groupCollapsed' in console

/**
 * Creates a context-aware logger that prefixes all log output with a label.
 *
 * When the runtime supports `console.groupCollapsed()` and multiple arguments
 * are provided, the first argument is used as the collapsed group title and the
 * remaining arguments are logged within the group. Otherwise, all arguments are
 * logged directly with the context prefix.
 *
 * @param context - The label used to prefix log messages. Defaults to `"Log"`.
 * @returns An object containing a `log` function for writing contextual log messages.
 *
 * @example
 * const logger = createLogger('Auth')
 *
 * logger.log('User signed in')
 * // [Auth] User signed in
 *
 * logger.log('Login response', response)
 * // Creates a collapsed group titled:
 * // [Auth] Login response
 * // ...with `response` logged inside the group (when supported).
 */
export const createLogger = (context: string = 'Log') => {
  const log = (...args: any[]) => {
    if (args.length > 1 && supportsCollapsedGroups) {
      console.groupCollapsed(`[${context}]`, args.shift())
      console.log(...args)
      console.groupEnd()
    } else {
      console.log(`[${context}]`, ...args)
    }
  }

  return {
    log,
  }
}
